import { useMemo, useState } from 'react'
import type { ChoiceExercise as ChoiceExerciseType, Exercise, MatchExercise as MatchExerciseType, OrderExercise as OrderExerciseType } from '../types/lesson'

type Props = {
  exercises: Exercise[]
  completedIds: string[]
  onComplete: (id: string, correct: boolean) => void
}

export function Exercises({ exercises, completedIds, onComplete }: Props) {
  return <div className="exercise-list">{exercises.map((exercise, index) => {
    const common = { key: exercise.id, number: index + 1, done: completedIds.includes(exercise.id), onComplete }
    if (exercise.type === 'choice') return <ChoiceExercise exercise={exercise} {...common} />
    if (exercise.type === 'match') return <MatchExercise exercise={exercise} {...common} />
    return <OrderExercise exercise={exercise} {...common} />
  })}</div>
}

function ExerciseHeader({ number, prompt, done }: { number: number; prompt: string; done: boolean }) {
  return <header className="exercise-header"><span>{number}</span><h3>{prompt}</h3>{done && <span className="done-mark" aria-label="Completed">✓</span>}</header>
}

type ExerciseComponentProps<T extends Exercise> = {
  exercise: T
  number: number
  done: boolean
  onComplete: (id: string, correct: boolean) => void
}

function ChoiceExercise({ exercise, number, done, onComplete }: ExerciseComponentProps<ChoiceExerciseType>) {
  const [selected, setSelected] = useState<string>()
  const isCorrect = selected === exercise.answer
  return (
    <section className="exercise-card">
      <ExerciseHeader number={number} prompt={exercise.prompt} done={done} />
      {exercise.pinyin && <p className="pinyin prompt-pinyin">{exercise.pinyin}</p>}
      <div className="choice-grid" role="group" aria-label={exercise.prompt}>
        {exercise.options.map((option: string) => <button key={option} className={`choice ${selected === option ? (isCorrect ? 'correct' : 'incorrect') : ''}`} onClick={() => { setSelected(option); onComplete(exercise.id, option === exercise.answer) }}>{option}</button>)}
      </div>
      {selected && <p className={`feedback ${isCorrect ? 'positive' : ''}`} role="status">{isCorrect ? 'Correct!' : 'Try again.'} {exercise.explanation}</p>}
    </section>
  )
}

function MatchExercise({ exercise, number, done, onComplete }: ExerciseComponentProps<MatchExerciseType>) {
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const options = useMemo(() => [...exercise.pairs.map((pair) => pair.right)].reverse(), [exercise.pairs])
  const allCorrect = exercise.pairs.every((pair) => answers[pair.left] === pair.right)

  return (
    <section className="exercise-card">
      <ExerciseHeader number={number} prompt={exercise.prompt} done={done} />
      <div className="match-list">
        {exercise.pairs.map((pair) => <label key={pair.left}><strong>{pair.left}</strong><select aria-label={`Choose the Pinyin for ${pair.left}`} value={answers[pair.left] || ''} onChange={(event) => setAnswers({ ...answers, [pair.left]: event.target.value })}><option value="">Choose Pinyin</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>)}
      </div>
      <button className="check-button" onClick={() => onComplete(exercise.id, allCorrect)} disabled={Object.keys(answers).length < exercise.pairs.length}>Check answers</button>
      {Object.keys(answers).length === exercise.pairs.length && <p className={`feedback ${allCorrect ? 'positive' : ''}`} role="status">{allCorrect ? 'Everything is correct!' : 'Not quite. Check the Pinyin and try again.'}</p>}
    </section>
  )
}

function OrderExercise({ exercise, number, done, onComplete }: ExerciseComponentProps<OrderExerciseType>) {
  const [remaining, setRemaining] = useState<string[]>(exercise.parts)
  const [chosen, setChosen] = useState<string[]>([])
  const isCorrect = chosen.join('|') === exercise.answer.join('|')
  const choose = (part: string, index: number) => { setChosen([...chosen, part]); setRemaining(remaining.filter((_, i) => i !== index)) }
  const reset = () => { setRemaining(exercise.parts); setChosen([]) }
  return (
    <section className="exercise-card">
      <ExerciseHeader number={number} prompt={exercise.prompt} done={done} />
      <div className="sentence-builder" aria-live="polite">{chosen.length ? chosen.map((part, i) => <span key={`${part}-${i}`}>{part}</span>) : <em>Tap the words to build the sentence</em>}</div>
      <div className="word-bank">{remaining.map((part, index) => <button key={`${part}-${index}`} onClick={() => choose(part, index)}>{part}</button>)}</div>
      <div className="exercise-actions"><button className="text-button" onClick={reset}>Start again</button><button className="check-button" onClick={() => onComplete(exercise.id, isCorrect)} disabled={remaining.length > 0}>Check answer</button></div>
      {remaining.length === 0 && <p className={`feedback ${isCorrect ? 'positive' : ''}`} role="status">{isCorrect ? 'Correct word order!' : 'That order is not correct yet.'} {exercise.explanation}</p>}
    </section>
  )
}
